import { call, select, takeEvery } from 'redux-saga/effects';
import dayjs from 'dayjs';
import { App } from '@ps20-frontend/frontend-models';
import { Feature } from '@ps20/bff-models';
import {
  BeginnAblaufGeaendert,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { IAppState } from '../localStore';
import { Produkt } from '../model';
import {
  selectVorvertragAblaufDatum,
  selectVorvertragLaufzeit,
} from '../selector/vorvertragLaufzeit.selector';
import { calculateMindestlaufzeitAblauf } from './mindestvertragslaufzeit.util';

interface VorvertragHauptfaelligkeit {
  iso: string;
  mmDD: string;
}

function pickAnyVorvertragHauptfaelligkeit(state: IAppState): VorvertragHauptfaelligkeit | null {
  for (const produkt of state.produkte ?? []) {
    if (!(produkt.vorvertragsnummer?.length > 0)) {
      continue;
    }
    const vorversicherung = state.vorversicherungen?.find(
      v => v.appId === produkt.appId && v.angebotsId === produkt.angebotsId,
    );
    const item = vorversicherung?.vorversicherungsListe?.[0];
    if (item?.ablaufdatum) {
      const parsed = dayjs(item.ablaufdatum, DATEFORMAT_LOCAL_STATE);
      if (parsed.isValid()) {
        return { iso: parsed.format(DATEFORMAT_SHORT_ISO), mmDD: parsed.format('DD.MM.') };
      }
    }
    const vorbelegungAblauf = selectVorvertragAblaufDatum(produkt);
    if (vorbelegungAblauf) {
      const parsed = dayjs(vorbelegungAblauf, DATEFORMAT_LOCAL_STATE);
      if (parsed.isValid()) {
        return { iso: parsed.format(DATEFORMAT_SHORT_ISO), mmDD: parsed.format('DD.MM.') };
      }
    }
  }
  return null;
}

function isRelevantForMindestlaufzeit(produkt: Produkt): boolean {
  return produkt.appId !== App.JHP && produkt.vorvertragsnummer?.length > 0;
}

function buildMindestlaufzeitPayloads(state: IAppState, mmDD: string): BeginnAblaufGeaendert[] {
  const payloads: BeginnAblaufGeaendert[] = [];
  for (const produkt of state.produkte ?? []) {
    if (!isRelevantForMindestlaufzeit(produkt)) {
      continue;
    }
    const ablauf = calculateMindestlaufzeitAblauf(
      produkt,
      produkt.beginn,
      selectVorvertragLaufzeit(produkt),
      selectVorvertragAblaufDatum(produkt),
      mmDD,
    );
    if (!ablauf) {
      continue;
    }
    payloads.push({
      appId: produkt.appId,
      angebotsId: produkt.angebotsId,
      beginn: produkt.beginn,
      ablauf,
    });
  }
  return payloads;
}

export function* handleMindestvertragslaufzeitCheckboxChanged(action: GlobalAction<boolean>) {
  const state: IAppState = yield select();
  if (!state.transientState?.featureToggles?.[Feature.MINDESTLAUFZEIT]) {
    return;
  }

  const globalPut = GlobalPut.getInstance();

  if (action.payload === true) {
    const hf = pickAnyVorvertragHauptfaelligkeit(state);
    if (!hf) {
      return;
    }
    yield call([globalPut, globalPut.put], new GlobalActions().hauptfaelligkeitChanged(hf.iso));
    const payloads = buildMindestlaufzeitPayloads(state, hf.mmDD);
    if (payloads.length > 0) {
      yield call(
        [globalPut, globalPut.put],
        new GlobalActions().updateBeginnAblaufOfProdukte(payloads),
      );
    }
    return;
  }

  yield call([globalPut, globalPut.put], new GlobalActions().initialHauptfaelligkeitRequested());
}

export function* watchMindestvertragslaufzeit() {
  yield takeEvery(
    GlobalActions.MINDESTVERTRAGSLAUFZEIT_CHECKBOX_CHANGED,
    safeSaga(handleMindestvertragslaufzeitCheckboxChanged, App.SHOP_CHECKOUT),
  );
}
